import { useEffect, useState } from "react";
import { manualDataFieldSections } from "../../forms/authFields";
import AuthDataFormSection from "./AuthDataFormSection";
import AuthDataFormSubSection from "./AuthDataFormSubSection";
import Button from "../common/Button";
import { useNavigate } from "react-router-dom";
import {
  camelCaseToLabel,
  getLocalDate,
  numberToWordsCrore,
  scriptRegex,
  validateField,
} from "../../utils/utils";
import {
  clearAllStorage,
  getStoredItem,
  setStoredItem,
} from "../../config/storageUtils";
import api from "../../api/ApiService";
import { INVEST_UK_WEBSITE_URL } from "../../config/apiConfig";
import { yojanaDropdownOptions } from "../../config/appliedSchemesConfig";
import Message from "../common/Message";
import ConfirmDialog from "../common/ConfirmDialog";
import { useLanguage } from "../LanguageProvider/LanguageProvider";
import { useAuth } from "./AuthProvider";

const ManualDataForm = () => {
  const [userDetails, setUserDetails] = useState({});
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();
  const [constructionStatus, setConstructionStatus] = useState("");
  const [availableIncentiveOptions, setAvailableIncentiveOptions] = useState(
    [],
  );
  const [apiMessage, setApiMessage] = useState(null);
  const { language, setLanguage } = useLanguage();
  const { setTokenGenerated, setUserType, setRoleId } = useAuth();

  useEffect(() => {
    buildInitialFormData();
  }, []);

  // only 3 fields count towards total (land excluded)
  const investTotalFields = [
    "investment_in_building",
    "investment_in_peq",
    "investment_in_other",
  ];

  const investAmountWordFields = [
    "investment_in_land",
    "investment_in_building",
    "investment_in_peq",
    "investment_in_other",
    "total_investment",
  ];

  const [disabledFields, setDisabledFields] = useState([]);

  const buildInitialFormData = async () => {
    const resp = await api.get(`/manual-data`);
    if (resp && resp.id > 0) {
      const manualData = JSON.parse(resp.details);
      setUserDetails(manualData || {});
    }
  };

  useEffect(() => {
    let initialData = {};
    let disabled = [];

    manualDataFieldSections.forEach((section) => {
      section.data?.forEach((subSection) => {
        if (subSection.title.en === "Investor Details") {
          // Handle Investor Details Array
          const investorTemplate = buildEmptyInvestor();

          if (userDetails?.investor_details?.length > 0) {
            // Prefill each investor
            initialData.investor_details = userDetails?.investor_details.map(
              (inv) => {
                let investorData = { ...investorTemplate };
                subSection.fields.forEach((field) => {
                  const fieldName = field.inputProps.name;
                  if (inv[fieldName] !== undefined && inv[fieldName] !== null) {
                    investorData[fieldName] = inv[fieldName];
                    disabled.push(`investor_details.${fieldName}`); // mark disabled uniquely
                  }
                });
                return investorData;
              },
            );
          } else {
            // Default 1 empty investor if none in userDetails
            initialData.investor_details = [investorTemplate];
          }
        } else {
          // Normal fields
          subSection.fields.forEach((field) => {
            const name = field.inputProps.name;
            if (
              userDetails &&
              userDetails[name] !== undefined &&
              userDetails[name] !== null &&
              userDetails["available_incentive_scheme"] !== ""
            ) {
              initialData[name] = userDetails[name];
              if (
                name === "available_incentive_scheme" ||
                name === "construction_status"
              ) {
                setConstructionStatus(userDetails["construction_status"]);
                if (
                  name === "construction_status" &&
                  initialData[name] === "completed"
                ) {
                  disabled.push(name);
                } else if (name === "available_incentive_scheme") {
                  setAvailableIncentiveOptions(yojanaDropdownOptions);
                  if (initialData[name] === "proayatanuddamiprotsahanyojna") {
                    disabled.push(name);
                  }
                }
              } else {
                disabled.push(name);
              }
            } else {
              initialData[name] = "";
            }
          });
        }
      });
    });

    investAmountWordFields.forEach((field) => {
      if (initialData[field]) {
        initialData[`${field}_in_words`] = numberToWordsCrore(
          initialData[field],
        );
      }
    });

    setFormData(initialData);
    setDisabledFields(disabled);

    const totalInvestment = parseFloat(initialData.total_investment) || 0;
    getAvailableIncentiveOptions(totalInvestment, initialData.investor_details);
  }, [userDetails]);

  const buildEmptyInvestor = () => {
    let investor = {};
    manualDataFieldSections.forEach((section) => {
      section.data?.forEach((subSection) => {
        if (subSection.title.en === "Investor Details") {
          subSection.fields.forEach((field) => {
            investor[field.inputProps.name] = "";
          });
        }
      });
    });
    return investor;
  };

  const getAvailableIncentiveOptions = (
    totalAmount = null,
    updatedInvestors = null,
  ) => {
    const totalInvestment =
      totalAmount ?? parseFloat(formData.total_investment) ?? 0;
    const investors = updatedInvestors ?? formData.investor_details ?? [];

    if (totalInvestment < 1) {
      setAvailableIncentiveOptions([]);
      return [];
    }

    const anyNonUK = investors.some(
      (inv) => inv.uttarakhandi_resident?.toLowerCase() === "no",
    );

    const options = [];

    if (!(totalInvestment < 5 && anyNonUK)) {
      if (totalInvestment >= 5) {
        options.push({
          label: "Uttarakhand Tourism Policy 2023",
          hi: "उत्तराखंड पर्यटन नीति 2023",
          value: "uttarakhandtourismpolicy2023",
        });
      }
      if (!anyNonUK) {
        options.push({
          label: "Uttarakhand Paryatan Udhyami Protsahan Yojana",
          hi: "उत्तराखंड पर्यटन उद्यमी प्रोत्साहन योजना",
          value: "prayatanuddamiprotsahanyojna",
        });
      }
    }

    setAvailableIncentiveOptions(options);
    return options;
  };

  // Handle regular fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));

    if (investTotalFields.includes(name)) {
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        updated[`${name}_in_words`] = numberToWordsCrore(value);

        const total = Object.keys(updated)
          .filter((key) => investTotalFields.includes(key)) // ✅ fixed bug (was using name)
          .reduce(
            (sum, key) => sum + (parseFloat(updated[key] || "0") || 0),
            0,
          );

        const newFormData = {
          ...updated,
          total_investment: Number(total.toFixed(6)),
          total_investment_in_words: numberToWordsCrore(
            Number(total.toFixed(6)),
          ),
        };

        // enforce ≥ 1 validation
        if (total < 1) {
          setErrors((prev) => ({
            ...prev,
            total_investment: {
              en: "Total Investment must be at least 1 Cr.",
              hi: "कुल निवेश कम से कम 1 करोड़ होना चाहिए",
            },
          }));
        } else {
          setErrors((prev) => {
            const { total_investment, ...rest } = prev;
            return rest;
          });
        }

        const updatedOptions = getAvailableIncentiveOptions(
          total,
          newFormData.investor_details,
        );
        if (
          newFormData.available_incentive_scheme &&
          !updatedOptions.some(
            (o) => o.value === newFormData.available_incentive_scheme,
          )
        ) {
          newFormData.available_incentive_scheme = "";
        }

        return newFormData;
      });
    }

    if (investAmountWordFields.includes(name)) {
      const updatedValue = value.replace(/,/g, ""); // remove commas for validation
      if (isNaN(updatedValue) && updatedValue !== "") {
        setErrors((prev) => ({
          ...prev,
          [name]: {
            en: "Please enter a valid number.",
            hi: "कृपया एक मान्य संख्या दर्ज करें।",
          },
        }));
        return;
      }
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        updated[`${name}_in_words`] = numberToWordsCrore(updatedValue);
        return updated;
      });
    }

    if (name === "construction_status") {
      setConstructionStatus(value);

      setDisabledFields((prev) => {
        let updatedDisabled = [...prev];

        if (value.toLowerCase() === "completed") {
          // remove completion_date_of_construction
          updatedDisabled = updatedDisabled.filter(
            (f) => f !== "completion_date_of_construction",
          );
        } else if (value.toLowerCase() === "under construction") {
          // remove both fields
          updatedDisabled = updatedDisabled.filter(
            (f) =>
              f !== "start_date_of_construction" &&
              f !== "expected_date_of_completion",
          );
        } else if (value.toLowerCase() === "construction not started") {
          // remove all three fields
          updatedDisabled = updatedDisabled.filter(
            (f) =>
              f !== "start_date_of_construction" &&
              f !== "expected_date_of_completion" &&
              f !== "completion_date_of_construction",
          );
        }

        return updatedDisabled;
      });

      setFormData((prev) => {
        let updatedForm = { ...prev, construction_status: value };

        if (
          value.toLowerCase() === "under construction" ||
          value.toLowerCase() === "construction not started"
        ) {
          updatedForm.start_date_of_construction = "";
          updatedForm.expected_date_of_completion = "";
          updatedForm.completion_date_of_construction = "";
          updatedForm.cto_pcb = "";
          updatedForm.noc_fire = "";
          updatedForm.travel_trade_registration = "";
          updatedForm.commercial_operation_date = "";
        }

        return updatedForm;
      });

      setErrors((prev) => {
        let updatedErrors = { ...prev };

        if (
          value.toLowerCase() === "under construction" ||
          value.toLowerCase() === "construction not started"
        ) {
          delete updatedErrors.start_date_of_construction;
          delete updatedErrors.expected_date_of_completion;
          delete updatedErrors.completion_date_of_construction;
          delete updatedErrors.cto_pcb;
          delete updatedErrors.noc_fire;
          delete updatedErrors.travel_trade_registration;
          delete updatedErrors.commercial_operation_date;
        }

        return updatedErrors;
      });
    }

    if (name === "total_investment") {
      const updatedOptions = getAvailableIncentiveOptions(
        parseFloat(value) || 0,
      );
      if (
        formData.available_incentive_scheme &&
        !updatedOptions.some(
          (o) => o.value === formData.available_incentive_scheme,
        )
      ) {
        setFormData((prev) => ({
          ...prev,
          available_incentive_scheme: "",
        }));
      }
    }
  };

  const validateDateField = (name, value, formData) => {
    const today = getLocalDate();

    if (name === "start_date_of_construction") {
      if (value > today) {
        return {
          en: "Start date must be before today",
          hi: "निर्माण प्रारंभ तिथि आज की तारीख से पहले होनी चाहिए",
        };
      }
      if (
        formData.expected_date_of_completion &&
        value >= formData.expected_date_of_completion
      ) {
        return {
          en: "Start date must be before Expected Completion date",
          hi: "निर्माण प्रारंभ तिथि अनुमानित पूर्णता तिथि से पहले होनी चाहिए",
        };
      }
    }

    if (name === "expected_date_of_completion") {
      if (
        formData.start_date_of_construction &&
        value <= formData.start_date_of_construction
      ) {
        return {
          en: "Expected Completion must be after Start date",
          hi: "अनुमानित पूर्णता तिथि निर्माण प्रारंभ तिथि के बाद होनी चाहिए",
        };
      }
    }

    if (name === "completion_date_of_construction") {
      if (value > today) {
        return {
          en: "Completion date must be before today",
          hi: "निर्माण पूर्णता तिथि आज की तारीख से पहले होनी चाहिए",
        };
      }
    }

    if (name === "commercial_operation_date") {
      if (value > today) {
        return {
          en: "Commercial Operation date must be before today",
          hi: "व्यावसायिक संचालन तिथि आज की तारीख से पहले होनी चाहिए",
        };
      }
    }

    return null; // ✅ no error
  };

  // Handle investor changes
  const handleInvestorChange = (index, name, value) => {
    setFormData((prev) => {
      const updatedInvestors = [...prev.investor_details];
      updatedInvestors[index] = {
        ...updatedInvestors[index],
        [name]: value,
      };

      const totalShareholding = updatedInvestors.reduce(
        (sum, inv) => sum + (parseFloat(inv.share_holding) || 0),
        0,
      );

      setErrors((prevErrors) => {
        const investorErrors = prevErrors.investor_details
          ? [...prevErrors.investor_details]
          : updatedInvestors.map(() => null);

        // 🔹 Validate only the current field
        const rowErrors = investorErrors[index]
          ? { ...investorErrors[index] }
          : {};

        if (name === "share_holding") {
          if (!value) {
            rowErrors.share_holding = {
              en: "Shareholding is required",
              hi: "शेयरहोल्डिंग आवश्यक है",
            };
          } else if (isNaN(value)) {
            rowErrors.share_holding = {
              en: "Must be a number",
              hi: "यह एक संख्या होनी चाहिए",
            };
          } else {
            delete rowErrors.share_holding;
          }
        }

        investorErrors[index] = Object.keys(rowErrors).length
          ? rowErrors
          : null;

        // 🔹 Enforce total = 100
        if (totalShareholding !== 100) {
          updatedInvestors.forEach((_, i) => {
            const totalErrorRow = investorErrors[i]
              ? { ...investorErrors[i] }
              : {};

            totalErrorRow.share_holding = {
              en: "Total shareholding across investors must equal 100%",
              hi: "सभी निवेशकों की कुल शेयरहोल्डिंग 100% होनी चाहिए",
            };

            investorErrors[i] = totalErrorRow;
          });
        } else {
          updatedInvestors.forEach((_, i) => {
            const err = investorErrors[i]?.share_holding;
            if (
              err?.en === "Total shareholding across investors must equal 100%"
            ) {
              const cleanedRow = { ...investorErrors[i] };
              delete cleanedRow.share_holding;
              investorErrors[i] = Object.keys(cleanedRow).length
                ? cleanedRow
                : null;
            }
          });
        }

        return { ...prevErrors, investor_details: investorErrors };
      });

      const updatedOptions = getAvailableIncentiveOptions(
        prev.total_investment,
        updatedInvestors,
      );

      if (
        formData.available_incentive_scheme &&
        !updatedOptions.some(
          (o) => o.value === formData.available_incentive_scheme,
        )
      ) {
        setFormData((prev) => ({
          ...prev,
          available_incentive_scheme: "",
        }));
      }

      return { ...prev, investor_details: updatedInvestors };
    });
  };

  const addInvestor = () => {
    setFormData((prev) => ({
      ...prev,
      investor_details: [...prev.investor_details, buildEmptyInvestor()],
    }));
  };

  const deleteInvestor = (index) => {
    const updatedInvestors = [...formData.investor_details];
    updatedInvestors.splice(index, 1);
    setFormData((prev) => ({ ...prev, investor_details: updatedInvestors }));
  };

  // Handle blur validation
  const handleBlur = (e, index = null) => {
    const { name, value } = e.target;

    let fieldConfig;
    manualDataFieldSections.forEach((section) => {
      section.data.forEach((subSection) => {
        subSection.fields.forEach((field) => {
          if (field.inputProps.name === name) {
            fieldConfig = field;
          }
        });
      });
    });

    if (fieldConfig?.inputProps?.validation) {
      const label = fieldConfig.label || camelCaseToLabel(name);
      let error = validateField(
        fieldConfig.inputProps.validation,
        value,
        label,
      );

      const dateError = validateDateField(name, value, formData);
      if (dateError) error = dateError;

      if (index !== null) {
        setErrors((prev) => {
          const updatedErrors = { ...prev };
          updatedErrors.investor_details = updatedErrors.investor_details || [];
          const existingRowErrors = updatedErrors.investor_details[index] || {};

          if (error) {
            // add/update error
            updatedErrors.investor_details[index] = {
              ...existingRowErrors,
              [name]: error,
            };
          } else {
            // remove error if value is valid
            const { [name]: removed, ...rest } = existingRowErrors;
            updatedErrors.investor_details[index] =
              Object.keys(rest).length > 0 ? rest : null;
          }

          return updatedErrors;
        });
      } else {
        setErrors((prev) => {
          const updated = { ...prev };
          if (error) {
            updated[name] = error;
          } else {
            delete updated[name];
          }
          return updated;
        });
      }
    }
  };

  const handleSubmit = async () => {
    let hasError = false;
    const normalErrors = {};

    manualDataFieldSections.forEach((section) => {
      section.data.forEach((subSection) => {
        if (
          (subSection.title.en === "Unit Operational Details" &&
            constructionStatus.toLowerCase() !== "completed") ||
          subSection.title.en === "Investor Details"
        )
          return;

        subSection.fields.forEach((field) => {
          const name = field.inputProps.name;

          if (
            name === "completion_date_of_construction" &&
            constructionStatus.toLowerCase() !== "completed"
          )
            return;
          if (
            (name === "start_date_of_construction" ||
              name === "expected_date_of_completion") &&
            constructionStatus.toLowerCase() !== "under construction"
          )
            return;

          const validation = field.inputProps.validation;
          if (!validation) return;

          const value = formData[name];
          const label = field.label || camelCaseToLabel(name);
          let error = validateField(validation, value, label);

          const dateError = validateDateField(name, value, formData);
          if (dateError) error = dateError;

          if (error) {
            normalErrors[name] = error;
            hasError = true;
          }
        });
      });
    });

    // enforce total_investment ≥ 1
    if (parseFloat(formData.total_investment || 0) < 1) {
      normalErrors.total_investment = {
        en: "Total Investment must be at least 1 Cr.",
        hi: "कुल निवेश कम से कम 1 करोड़ होना चाहिए",
      };
      hasError = true;
    }

    setErrors((prev) => ({
      ...prev,
      ...normalErrors,
    }));

    const investorFieldsConfig = manualDataFieldSections
      .find((s) => s.data.some((sub) => sub.title.en === "Investor Details"))
      ?.data.find((sub) => sub.title.en === "Investor Details")?.fields;

    // Validate each investor row
    let investorErrors = formData.investor_details.map((investor) => {
      const rowErrors = {};
      investorFieldsConfig.forEach((field) => {
        const validation = field.inputProps.validation;
        if (!validation) return;

        const value = investor[field.inputProps.name];
        const label = field.label || camelCaseToLabel(field.inputProps.name);
        const error = validateField(validation, value, label);
        if (error) {
          rowErrors[field.inputProps.name] = error;
          hasError = true;
        }
      });
      return Object.keys(rowErrors).length ? rowErrors : null;
    });

    // 🔹 Enforce total shareholding = 100
    const totalShareholding = formData.investor_details.reduce(
      (sum, inv) => sum + (parseFloat(inv.share_holding) || 0),
      0,
    );
    if (totalShareholding !== 100) {
      investorErrors = investorErrors.map((row) => ({
        ...row,
        share_holding: {
          en: "Total shareholding across investors must equal 100%",
          hi: "सभी निवेशकों की कुल हिस्सेदारी 100% होनी चाहिए",
        },
      }));
      hasError = true;
    }

    setErrors((prev) => ({
      ...prev,
      investor_details: investorErrors,
    }));

    if (!hasError) {
      try {
        const resp = await api.post("/manual-data", {
          ...formData,
          city: "",
        });

        if (resp.status === 201 || resp.status === 200) {
          if (
            formData.construction_status.toLowerCase() !== "completed" ||
            formData.available_incentive_scheme ===
              "uttarakhandtourismpolicy2023"
          ) {
            scrollTo(0, 0);
            setShowDialog(true);
          } else {
            setTokenGenerated(true);
            navigate("/dashboard");
          }
        } else {
          setApiMessage({
            type: "error",
            message:
              error.response?.data?.message ||
              "Error submitting form. Please try again.",
          });
        }
      } catch (error) {
        setApiMessage({
          type: "error",
          message:
            error.response?.data?.message ||
            "Error submitting form. Please try again.",
        });
      }
    }
  };

  const handleConfirmData = async () => {
    setShowDialog(false);
    await clearAllStorage();
    setUserType(null);
    setTokenGenerated(false);
    setRoleId(null);
    if (
      formData.available_incentive_scheme === "uttarakhandtourismpolicy2023"
    ) {
      window.open(INVEST_UK_WEBSITE_URL, "_self");
      return;
    }
    navigate("/", { replace: true });
  };

  return (
    <>
      <>
        {apiMessage && (
          <Message
            apiMessage={apiMessage}
            setApiMessage={setApiMessage}
          ></Message>
        )}
        {manualDataFieldSections.map((section, index) => (
          <AuthDataFormSection
            key={index}
            title={language ? section.title.hi : section.title.en}
          >
            {section?.data.map((subSection, subIndex) => {
              if (subSection.title.en === "Investor Details") {
                return (
                  <div key={subIndex}>
                    {formData?.investor_details?.map((investor, i) => (
                      <div
                        key={i}
                        className="border p-4 mb-4 rounded shadow-sm"
                      >
                        <div className="flex items-start">
                          {/* Inputs (take most width) */}
                          <div className="flex-1">
                            <AuthDataFormSubSection
                              sectionHeading={`${
                                language
                                  ? subSection.title.hi
                                  : subSection.title.en
                              } ${i + 1}`}
                              sectionFields={subSection.fields.map((field) => {
                                const fieldName = field.inputProps.name;

                                // ✅ Pull value from userDetails?.investor_details[i] if exists
                                const prefilledValue =
                                  userDetails?.investor_details?.[i]?.[
                                    fieldName
                                  ] ?? "";

                                // ✅ Disable if userDetails already has value for that field
                                const isDisabled =
                                  prefilledValue !== "" &&
                                  prefilledValue !== undefined &&
                                  prefilledValue !== null;

                                return {
                                  ...field,
                                  inputProps: {
                                    ...field.inputProps,
                                    value:
                                      investor?.[fieldName] ??
                                      prefilledValue ??
                                      field.inputProps.value ??
                                      "",
                                    //disabled: isDisabled,
                                  },
                                };
                              })}
                              formData={investor}
                              handleChange={(e) => {
                                if (!scriptRegex.test(e.target.value)) {
                                  handleInvestorChange(
                                    i,
                                    e.target.name,
                                    e.target.value,
                                  );
                                }
                              }}
                              handleBlur={(e) => handleBlur(e, i)}
                              errors={errors?.investor_details?.[i] || {}}
                              language={language}
                            />
                          </div>

                          {/* Buttons aligned with first textbox */}
                          {/* {!userDetails?.investor_details?.length && ( */}
                          <div className="flex flex-col gap-2 ml-4 mt-12">
                            <Button
                              className="bg-btn font-semibold px-3 py-1 rounded"
                              onClick={addInvestor}
                            >
                              {language ? "नई पंक्ति जोड़" : "Add"}
                            </Button>
                            {formData?.investor_details?.length > 1 && (
                              <Button
                                className="bg-red-500 text-white px-3 py-1 rounded"
                                onClick={() => deleteInvestor(i)}
                              >
                                {language ? "हटाएं" : "Delete"}
                              </Button>
                            )}
                          </div>
                          {/* )} */}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }

              if (
                subSection.title.en === "Unit Operational Details" &&
                constructionStatus.toLowerCase() !== "completed"
              ) {
                return null;
              }

              return (
                <AuthDataFormSubSection
                  key={subIndex}
                  sectionHeading={
                    language ? subSection.title.hi : subSection.title.en
                  }
                  sectionFields={subSection.fields
                    .filter((field) => {
                      if (
                        field.inputProps.name ===
                        "completion_date_of_construction"
                      ) {
                        return constructionStatus.toLowerCase() === "completed";
                      }
                      if (
                        field.inputProps.name ===
                          "start_date_of_construction" ||
                        field.inputProps.name === "expected_date_of_completion"
                      ) {
                        return (
                          constructionStatus.toLowerCase() ===
                          "under construction"
                        );
                      }
                      return true;
                    })
                    .map((field) => {
                      if (
                        field.inputProps.name === "available_incentive_scheme"
                      ) {
                        return {
                          ...field,
                          inputProps: {
                            ...field.inputProps,
                            options: availableIncentiveOptions,
                            value: formData.available_incentive_scheme,
                            // disabled: disabledFields.includes(
                            //   field.inputProps.name,
                            // ),
                          },
                        };
                      }
                      return {
                        ...field,
                        inputProps: {
                          ...field.inputProps,
                          // disabled: disabledFields.includes(
                          //   field.inputProps.name,
                          // ),
                        },
                      };
                    })}
                  formData={formData}
                  handleChange={(e) => {
                    if (!scriptRegex.test(e.target.value)) {
                      handleChange(e);
                    }
                  }}
                  handleBlur={handleBlur}
                  errors={errors}
                  language={language}
                />
              );
            })}
          </AuthDataFormSection>
        ))}

        <div className="flex justify-end">
          <Button
            className="bg-btn font-semibold py-2 px-4 rounded-md"
            onClick={handleSubmit}
          >
            {language ? "जमा करें" : "Submit"}
          </Button>
        </div>
      </>
      <ConfirmDialog
        isOpen={showDialog}
        title={language ? "सफलता" : "Success"}
        message={
          formData.available_incentive_scheme === "uttarakhandtourismpolicy2023"
            ? {
                en: "Your intent to avail incentive has been received. Please check eligibility and submit your application on the Invest Uttarakhand portal.",
                hi: "प्रोत्साहन का लाभ लेने का आपका अनुरोध प्राप्त हो गया है। कृपया पात्रता जांचें और Invest Uttarakhand पोर्टल पर आवेदन करें।",
              }
            : constructionStatus.toLowerCase() === "construction not started"
              ? {
                  en: "Application saved successfully with Construction Not Started status. Once construction starts, please re-login to update your construction status.",
                  hi: "आवेदन 'निर्माण प्रारंभ नहीं हुआ' स्थिति के साथ सफलतापूर्वक सहेजा गया है। निर्माण शुरू होने पर कृपया दोबारा लॉगिन कर स्थिति अपडेट करें।",
                }
              : {
                  en: "Application saved successfully with Under Construction status. After completion, please re-login to update details and apply for claim.",
                  hi: "आवेदन 'निर्माणाधीन' स्थिति के साथ सफलतापूर्वक सहेजा गया है। पूर्ण होने के बाद विवरण अपडेट कर दावा करने के लिए पुनः लॉगिन करें।",
                }
        }
        onConfirm={handleConfirmData}
        onCancel={() => setShowDialog(false)}
        cancelVisible={false}
        buttonText={{ en: "OK", hi: "ठीक है" }}
        language={language}
      />
    </>
  );
};

export default ManualDataForm;
